import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { channels } from "@/mocks/channels";
import { topics } from "@/mocks/topics";

export default function Navbar() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<"channel" | "topic" | null>(null);
  const [keyword, setKeyword] = useState("");

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
    setMenuOpen(false);
  };

  const closeAll = () => {
    setMenuOpen(false);
    setOpenDropdown(null);
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur border-b border-background-200/70">
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 shrink-0" onClick={closeAll} aria-label="直播 首页">
            <span className="w-9 h-9 rounded-md bg-primary-500 flex items-center justify-center">
              <i className="ri-live-line text-xl text-background-50" />
            </span>
            <span className="font-heading text-xl font-extrabold text-foreground-950 tracking-tight">
              直播
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="px-3 py-2 rounded-md text-sm font-label text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors"
            >
              首页
            </Link>

            <div
              className="relative"
              onMouseEnter={() => setOpenDropdown("channel")}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <button
                type="button"
                className="flex items-center gap-1 px-3 py-2 rounded-md text-sm font-label text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors"
              >
                频道
                <i className="ri-arrow-down-s-line text-base" />
              </button>
              {openDropdown === "channel" && (
                <div className="absolute left-0 top-full pt-2 w-72">
                  <div className="rounded-lg border border-background-200/70 bg-background-50 p-2 grid grid-cols-2 gap-1">
                    {channels.map((channel) => (
                      <Link
                        key={channel.key}
                        to={`/category/${channel.key}`}
                        onClick={closeAll}
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                      >
                        <i className={`${channel.icon} text-base`} />
                        {channel.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div
              className="relative"
              onMouseEnter={() => setOpenDropdown("topic")}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <button
                type="button"
                className="flex items-center gap-1 px-3 py-2 rounded-md text-sm font-label text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors"
              >
                专题
                <i className="ri-arrow-down-s-line text-base" />
              </button>
              {openDropdown === "topic" && (
                <div className="absolute left-0 top-full pt-2 w-64">
                  <div className="rounded-lg border border-background-200/70 bg-background-50 p-2 flex flex-col gap-1">
                    {topics.map((topic) => (
                      <Link
                        key={topic.id}
                        to={`/topic/${topic.id}`}
                        onClick={closeAll}
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                      >
                        <i className="ri-fire-line text-base text-accent-500" />
                        {topic.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/about"
              className="px-3 py-2 rounded-md text-sm font-label text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors"
            >
              关于我们
            </Link>
            <Link
              to="/contact"
              className="px-3 py-2 rounded-md text-sm font-label text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors"
            >
              联系反馈
            </Link>
          </nav>

          <form onSubmit={handleSearch} className="hidden md:flex items-center w-64">
            <div className="relative w-full">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                type="text"
                placeholder="搜索直播或主播"
                className="w-full rounded-full border border-background-300/60 bg-background-100 pl-9 pr-4 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
              />
            </div>
          </form>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 transition-colors"
          >
            <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50 px-4 pb-5 pt-4">
          <form onSubmit={handleSearch} className="mb-4">
            <div className="relative w-full">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                type="text"
                placeholder="搜索直播或主播"
                className="w-full rounded-full border border-background-300/60 bg-background-100 pl-9 pr-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>
          </form>
          <nav className="flex flex-col gap-1">
            <Link to="/" onClick={closeAll} className="rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-50">
              首页
            </Link>
            <p className="px-3 pt-2 pb-1 text-xs font-label text-foreground-400">频道</p>
            <div className="grid grid-cols-2 gap-1">
              {channels.map((channel) => (
                <Link
                  key={channel.key}
                  to={`/category/${channel.key}`}
                  onClick={closeAll}
                  className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-50"
                >
                  <i className={`${channel.icon} text-base`} />
                  {channel.name}
                </Link>
              ))}
            </div>
            <p className="px-3 pt-3 pb-1 text-xs font-label text-foreground-400">专题</p>
            {topics.map((topic) => (
              <Link
                key={topic.id}
                to={`/topic/${topic.id}`}
                onClick={closeAll}
                className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-50"
              >
                <i className="ri-fire-line text-base text-accent-500" />
                {topic.title}
              </Link>
            ))}
            <Link to="/about" onClick={closeAll} className="rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-50">
              关于我们
            </Link>
            <Link to="/contact" onClick={closeAll} className="rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-50">
              联系反馈
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}